import { Controller } from "@hotwired/stimulus";

// Reads a week of ziyaret saatleri on the place's own clock and says whether the
// doors are open right now:
//
//   <section data-controller="opening-hours"
//            data-opening-hours-time-zone-value="Europe/Istanbul"
//            data-opening-hours-week-value='{"1":["09:00","19:00"], …}'>
//     <span data-opening-hours-target="state" data-open>Şu an açık</span>
//     <span data-opening-hours-target="today">09:00 – 19:00</span>
//     <div data-opening-hours-target="day" data-day="1">…</div>
//
// The server renders the same answer, so the section is already right without
// JavaScript — and stays right afterwards, since a cached or long-open page
// would otherwise keep announcing that a museum is open at midnight. The day
// keys are ISO (1 = Pazartesi … 7 = Pazar) and a closing time at or before the
// opening one runs past midnight, which is the reading the model makes too.
export default class extends Controller {
  static targets = ["state", "today", "day"];
  static values = { timeZone: String, week: Object };

  connect() {
    this.tick();
    // The answer only changes on the minute, and only twice a day at that.
    this.timer = setInterval(() => this.tick(), 30000);
  }

  disconnect() {
    clearInterval(this.timer);
  }

  tick() {
    const now = this.#localParts();
    const today = this.weekValue[now.day];

    if (this.hasTodayTarget) {
      this.todayTarget.textContent = today
        ? `${today[0]} – ${today[1]}`
        : "Bugün kapalı";
    }

    if (this.hasStateTarget) {
      const open = this.#openAt(now);
      this.stateTarget.textContent = open ? "Şu an açık" : "Şu an kapalı";
      // The look is the markup's: the badge and the tile around it are styled
      // off this attribute with Tailwind's data-open: variant.
      this.stateTarget.toggleAttribute("data-open", open);
    }

    this.dayTargets.forEach((row) => {
      row.classList.toggle("font-medium", row.dataset.day === String(now.day));
      row.classList.toggle("text-gray-900", row.dataset.day === String(now.day));
    });
  }

  // Today's row, and yesterday's while it is still running past midnight.
  #openAt({ day, minutes }) {
    const yesterday = day === 1 ? 7 : day - 1;
    return (
      this.#within(this.weekValue[day], minutes) ||
      this.#within(this.weekValue[yesterday], minutes + 1440)
    );
  }

  #within(hours, minutes) {
    if (!hours) return false;

    const opens = this.#minutes(hours[0]);
    let closes = this.#minutes(hours[1]);
    if (closes <= opens) closes += 1440;
    return minutes >= opens && minutes < closes;
  }

  #minutes(clock) {
    const [hour, minute] = clock.split(":");
    return Number(hour) * 60 + Number(minute);
  }

  // The day and the time where the place is, not where the reader is.
  #localParts() {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: this.timeZoneValue,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(new Date());

    const read = (type) => parts.find((part) => part.type === type).value;
    const days = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

    return {
      day: days[read("weekday")],
      minutes: Number(read("hour")) * 60 + Number(read("minute")),
    };
  }
}
